import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Gift, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email, name });
      toast.success("Check your inbox for a small gift ✨");
      setSubmitted(true);
    } catch {
      toast.error("Something interrupted that. Try again?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="relative py-28 md:py-36 overflow-hidden" data-testid="lead-magnet-section">
      <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] bg-peach/35 organic-blob blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-10 w-[400px] h-[400px] bg-lavender/40 organic-blob blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.2, 0.7, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute -inset-4 bg-sage/30 organic-blob blur-2xl" />
            <div className="relative glass-strong rounded-[2rem] p-8 md:p-10 text-left">
              <Gift size={36} className="text-peach-deep mb-5" strokeWidth={1.3} />
              <div className="overline mb-3">A free download</div>
              <h3 className="font-serif text-3xl md:text-4xl text-lapis leading-tight font-light">
                Seven affirmations<br />for the <em className="text-peach-deep">soft days</em>.
              </h3>
              <div className="mt-6 space-y-2.5 text-sm text-ink/70">
                {["I am safe in this present moment.", "My becoming is unfolding, exactly on time.", "I am worthy, exactly as I am.", "+ four more, hand-poured."].map((a, i) => (
                  <div key={i} className="flex items-baseline gap-2 font-serif italic">
                    <Sparkles size={10} className="text-peach-deep mt-1 flex-shrink-0" />
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <div className="overline mb-5">A small gift, from me</div>
          <h2 className="font-serif text-4xl md:text-5xl text-ink font-light leading-tight">
            Begin with a <em className="text-peach-deep">soft anchor</em>.
          </h2>
          <p className="mt-6 text-ink/70 leading-relaxed max-w-lg">
            Drop your email and I'll send you the seven affirmations I return to whenever the noise gets loud.
            They arrive in a few seconds — no spam, only what's needed.
          </p>

          {submitted ? (
            <div className="mt-10 glass p-6 rounded-[1.5rem]" data-testid="lead-magnet-success">
              <div className="font-serif text-2xl text-lapis italic">It's on its way ✨</div>
              <div className="mt-2 text-sm text-ink/65">
                Check your inbox (and maybe the promotions folder, just in case). You're in the circle now.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 grid grid-cols-1 sm:grid-cols-12 gap-4 max-w-lg" data-testid="lead-magnet-form">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="eth-input sm:col-span-5"
                data-testid="lead-magnet-name"
              />
              <input
                type="email"
                required
                placeholder="your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="eth-input sm:col-span-7"
                data-testid="lead-magnet-email"
              />
              <button
                type="submit"
                disabled={loading}
                className="btn-primary sm:col-span-12 justify-center mt-2"
                data-testid="lead-magnet-submit"
              >
                {loading ? "Sending…" : "Send me the affirmations"}
              </button>
            </form>
          )}

          <p className="mt-6 text-xs text-ink/50 italic">
            Unsubscribe anytime — no questions, no guilt.
          </p>
        </div>
      </div>
    </section>
  );
}
